import { useEffect, useRef, useState } from "react";
import Login from "./Login";
import MainPage from "./MainPage";
import ErrorBoundary from "./components/ErrorBoundary";
import ErrorDialog from "./components/ErrorDialog";
import GenerationView from "./components/GenerationView";
import Loader from "./components/Loader";
import { SESSION_ENDED, onSessionEnded } from "./apiShim";
import { DEFAULT_SCALE, PALETTES, clampScale, useTheme } from "./theme";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

// Check once at module load — not inside the component — so hooks order is stable.
const IS_GENERATION_TAB = window.location.hash === "#/generate";

const SIGN_OUT_TIMEOUT_MS = 15000;
export const SIGN_OUT_FAILED =
  "Sign out didn't go through, so your session is still active. Try again before you leave this computer.";

function App() {
  const { userkey, setColor, setMode, setScale, setUsername, setEmail, setUserkey, setUserid } = useTheme();
  // The session cookie is what signs you in (AUTH-01); the API reads it on
  // GET users/me below. The name and userkey kept in sessionStorage only let a
  // refresh show the app at once instead of the login page while that is asked.
  const [isAuthed, setIsAuthed] = useState(() => Boolean(userkey));
  // With nothing stored, show nothing until the API has answered, so a new tab
  // of a signed-in browser doesn't flash the login page.
  const [checking, setChecking] = useState(() => !userkey && !IS_GENERATION_TAB);
  const [notice, setNotice] = useState("");
  const [signingOut, setSigningOut] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  const authedRef = useRef(isAuthed);
  authedRef.current = isAuthed;


  const DEFAULT_COLOR = "blue";
  const DEFAULT_MODE = "light";

  const resetThemeToDefault = () => {
    setColor(DEFAULT_COLOR);
    setMode(DEFAULT_MODE);
    setScale(DEFAULT_SCALE);
  };

  const applyThemeFromPreferences = async (userkey) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}userpreferences/user/${userkey}/page/main`
      );
      if (!res.ok) {
        // No saved preferences yet for this user — use defaults.
        resetThemeToDefault();
        return;
      }
      const body = await res.json();
      const savedMode = body?.data?.settings?.mode;
      const savedColor = body?.data?.settings?.color || body?.data?.settings?.theme;
      setMode(savedMode === "dark" ? "dark" : DEFAULT_MODE);
      setColor(savedColor && PALETTES[savedColor] ? savedColor : DEFAULT_COLOR);
      // One of the seven sizes (an older "scaleStep" is ignored).
      setScale(clampScale(body?.data?.settings?.scale ?? DEFAULT_SCALE));
    } catch {
      // Network / parse error — fall back to defaults so the UI is never
      // left showing the previous user's accent color.
      resetThemeToDefault();
    }
  };

  const handleLogin = (user) => {
    setUsername(user?.username);
    setEmail(user?.emailid);
    setUserkey(user?.userkey);
    setUserid(user?.userid);
    setNotice("");
    setIsAuthed(true);
    if (user?.userkey) applyThemeFromPreferences(user.userkey);
    else resetThemeToDefault();
  };

  const signOut = (message = "") => {
    setUsername(undefined);
    setEmail(undefined);
    setUserkey(undefined);
    setUserid(undefined);
    resetThemeToDefault();
    setNotice(message);
    setIsAuthed(false);
  };

  // On mount: ask the API who the session cookie belongs to. Signed in, the
  // user's details and server-side preferences are read again (a theme changed
  // on another device flows through); a 401 signs out. When the API can't be
  // reached, what is shown stays as it is.
  useEffect(() => {
    if (IS_GENERATION_TAB) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}users/me`);
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && body?.data?.userkey) handleLogin(body.data);
        else if (res.status === 401 && authedRef.current) signOut(SESSION_ENDED);
      } catch {
        /* API unreachable: leave the screen as it is */
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any API call answering 401 means the session has ended (apiShim.js).
  useEffect(
    () =>
      onSessionEnded(() => {
        if (authedRef.current) signOut(SESSION_ENDED);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Only the API can clear the HttpOnly session cookie, so the login page shows
  // only once it has answered. If it can't, the session still works: a reload
  // would sign straight back in, so the user is told and stays where they are.
  const handleLogout = async () => {
    setSigningOut(true);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SIGN_OUT_TIMEOUT_MS);
    let ended = false;
    try {
      const res = await fetch(`${API_BASE_URL}users/logout`, {
        method: "POST",
        signal: controller.signal,
      });
      ended = res.ok;
    } catch {
      /* unreachable or timed out: still signed in */
    } finally {
      clearTimeout(timer);
      setSigningOut(false);
    }
    if (ended) signOut();
    else setSignOutFailed(true);
  };

  // Generation tab: render standalone GenerationView (config comes from
  // localStorage set by the opener tab; the session cookie is shared).
  if (IS_GENERATION_TAB) {
    return (
      <ErrorBoundary name="Generation">
        <GenerationView />
      </ErrorBoundary>
    );
  }

  if (checking) {
    return null;
  }

  if (!isAuthed) {
    return <Login onLogin={handleLogin} notice={notice} />;
  }

  return (
    <>
      {/* FE-02: a render error the screens' own boundaries don't catch (the
          header, the sidebar) shows a card instead of a blank page. The
          sign-out overlay and dialog stay outside it, so they always show. */}
      <ErrorBoundary name="The page">
        <MainPage onLogout={handleLogout} />
      </ErrorBoundary>
      {signingOut && <Loader.Overlay variant="guide" label="Signing out…" sublabel="" />}
      {signOutFailed && (
        <ErrorDialog
          title="You are still signed in"
          message={SIGN_OUT_FAILED}
          onClose={() => setSignOutFailed(false)}
        />
      )}
    </>
  );
}

export default App;
